<template>
  <section class="executive-report-root" ref="reportRoot">
    <template v-if="report">
    <!-- 1 Cover -->
    <section class="er-page er-cover" v-bind="pageAttrsFor('cover')">
      <div class="er-cover__inner">
        <header class="er-cover__letterhead">
          <img
            :src="buzzzLogo"
            :alt="report.branding.productName"
            class="er-cover__brand-logo"
            width="138"
            height="66"
          />
          <div class="er-cover__client">
            <img
              v-if="report.branding.logoUrl"
              :src="report.branding.logoUrl"
              crossorigin="anonymous"
              alt=""
              class="er-cover__logo"
            />
            <div class="er-cover__company">{{ report.branding.companyName || report.meta.companyName }}</div>
            <div class="er-cover__date">{{ reportDateLabel }}</div>
          </div>
        </header>

        <div class="er-cover__hero">
          <img :src="coverHero" alt="" class="er-cover__hero-img" />
          <div class="er-cover__hero-fade"></div>
          <div class="er-cover__caption">
            <p class="er-cover__subject">{{ coverTitle }}</p>
            <div class="er-cover__period">{{ periodLabel }}</div>
            <h1 class="er-cover__site-name">{{ report.meta.siteName }}</h1>
            <div class="er-cover__rule"></div>
            <p class="er-cover__shift" v-if="report.meta.shiftNames.length">{{ report.meta.shiftNames.join(', ') }}</p>
          </div>
        </div>

        <div class="er-cover__headlines" :class="`er-cover__headlines--${coverHeadlines.length}`">
          <article v-for="item in coverHeadlines" :key="item.metricKey" class="er-cover__headline">
            <div class="er-cover__headline-value">
              <span :dir="item.format === 'duration' ? 'ltr' : null">{{ formatValue(item.current, item.format) }}</span><span v-if="item.unit">{{ item.unit }}</span>
            </div>
            <div class="er-cover__headline-label">{{ $t(item.metricKey) }}</div>
            <div
              v-if="hasComparison && item.incDec"
              class="er-cover__headline-change"
              :class="incDecClass(item)"
            >{{ item.incDec }}</div>
            <div class="er-cover__spark" v-if="item.series.length">
              <Chart
                type="line"
                :labels="chartLabels(item.series)"
                :datasets="sparkDataset(item.series, item.color)"
                :options="sparkOptionsFor(item.series)"
                height="44px"
                width="100%"
              />
            </div>
          </article>
        </div>

        <p class="er-cover__chart-window" v-if="showCoverChartCaption">{{ singleMonthChartCaption }}</p>

        <div class="er-cover__highlights" v-if="coverHighlights.length">
          <p class="er-cover__highlights-title">{{ tPeriod('er_cover_highlights') }}</p>
          <div
            class="er-cover__highlights-row"
            :class="`er-cover__highlights-row--${coverHighlights.length}`"
          >
            <p
              v-for="(item, idx) in coverHighlights"
              :key="item.id"
              class="er-cover__highlight"
              :class="[
                item.tone,
                { 'is-first': idx === 0, 'is-last': idx === coverHighlights.length - 1 },
              ]"
            >
              {{ item.text }}
            </p>
          </div>
        </div>
      </div>
      <executive-report-page-footer
        :parts="contactLineParts"
        :copy="footerText"
        :rtl="isRTL"
        :last-page="isLastPage('cover')"
      />
    </section>

    <!-- 2 What changed this period -->
    <section class="er-page" v-bind="pageAttrsFor('findings')" v-if="showFindingsPage">
      <h2 class="er-heading">{{ tPeriod('er_page_success_metrics') }}</h2>
      <p class="er-page-note">{{ $t('er_findings_intro', { month: monthFull('comparison') }) }}</p>

      <div class="er-findings-board" v-for="board in findingBoards" :key="board.severity">
        <div class="er-findings-board__label">
          <span class="er-pill" :class="`er-pill--${board.severity}`">{{ $t(board.pillKey) }}</span>
          <h3>{{ $t(board.titleKey, { month: monthFull('comparison') }) }}</h3>
        </div>
        <div class="er-findings-list">
          <article v-for="item in board.items" :key="item.id" class="er-finding-row">
            <div
              class="er-finding-row__index"
              :class="{ 'er-finding-row__index--risk': board.severity === 'risk' }"
              aria-hidden="true"
            ></div>
            <div class="er-finding-row__body">
              <div class="er-finding-row__head">
                <h4>{{ $t(item.titleKey) }}</h4>
                <span class="er-verdict" :class="`er-verdict--${board.severity}`">{{ $t(item.verdictKey) }}</span>
              </div>
              <p>{{ sentence(item.sentences && item.sentences[0]) }}</p>
              <p
                v-for="(part, idx) in findingDetails(item)"
                :key="`${item.id}-d-${idx}`"
                class="er-finding-row__detail"
              >
                {{ sentence(part) }}
              </p>
            </div>
          </article>
        </div>
      </div>

      <p class="er-board-empty" v-if="!successFindings.length && !riskFindings.length">
        {{ tPeriod('er_board_empty') }}
      </p>

      <executive-report-page-footer
        :parts="contactLineParts"
        :copy="footerText"
        :rtl="isRTL"
        :last-page="isLastPage('findings')"
      />
    </section>

    <!-- 3 Comparison Summary -->
    <section class="er-page" v-bind="pageAttrsFor('comparison')" v-if="printComparisonRows.length">
      <h2 class="er-heading">{{ comparisonPageTitle }}</h2>
      <p class="er-page-note" v-if="comparisonPageNote">{{ comparisonPageNote }}</p>
      <p class="er-page-note" v-if="showLifetimeColumn">{{ $t('er_lifetime_note', { month: monthEnd('current') }) }}</p>
      <table class="er-table" :class="{ 'er-table--averages': showAverageColumn || showLifetimeColumn }">
        <thead>
          <tr>
            <th>{{ $t('er_col_metric') }}</th>
            <th v-if="hasComparison">{{ monthFull('comparison') }}</th>
            <th>{{ monthFull('current') }}</th>
            <th v-if="showAverageColumn">{{ $t('er_col_avg') }}</th>
            <th v-if="showLifetimeColumn">{{ $t('er_col_lifetime_avg') }}</th>
            <th v-if="hasComparison">{{ $t('er_col_inc_dec') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in printComparisonRows" :key="row.metricKey">
            <td class="er-table__metric">
              <span class="er-table__swatch" :style="{ background: metricColor(row.metricKey) }"></span>
              {{ $t(row.metricKey) }}
            </td>
            <td v-if="hasComparison">{{ formatValue(row.previous, row.format) }}</td>
            <td>{{ formatValue(row.current, row.format) }}</td>
            <td v-if="showAverageColumn">{{ formatValue(row.average, row.format) }}</td>
            <td v-if="showLifetimeColumn">{{ formatValue(row.lifetimeAverage, row.format) }}</td>
            <td v-if="hasComparison" :class="incDecClass(row)">{{ row.incDec }}</td>
          </tr>
        </tbody>
      </table>

      <executive-report-page-footer
        :parts="contactLineParts"
        :copy="footerText"
        :rtl="isRTL"
        :last-page="isLastPage('comparison')"
      />
    </section>

    <section
      class="er-page"
      v-for="(pageMonths, pageIdx) in monthBreakdownPages"
      :key="`months-${pageIdx}`"
      v-bind="pageAttrsFor(`months-${pageIdx}`)"
    >
      <h2 class="er-heading">{{ $t('er_page_each_month') }}</h2>
      <p class="er-page-note">{{ $t('er_each_month_note') }}</p>
      <table class="er-table er-table--months">
        <thead>
          <tr>
            <th class="er-table__metric">{{ $t('er_col_metric') }}</th>
            <th v-for="month in pageMonths" :key="monthKey(month)">{{ monthBreakdownLabel(month) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="metric in monthBreakdownMetrics" :key="metric.metricKey">
            <td class="er-table__metric">{{ $t(metric.metricKey) }}</td>
            <td v-for="month in pageMonths" :key="`${monthKey(month)}-${metric.metricKey}`">
              {{ monthCell(month, metric.metricKey) }}
            </td>
          </tr>
        </tbody>
      </table>

      <executive-report-page-footer
        :parts="contactLineParts"
        :copy="footerText"
        :rtl="isRTL"
        :last-page="isLastPage(`months-${pageIdx}`)"
      />
    </section>

    <section
      class="er-page"
      v-for="(pageShifts, pageIdx) in shiftComparisonPages"
      :key="`shifts-${pageIdx}`"
      v-bind="pageAttrsFor(`shifts-${pageIdx}`)"
    >
      <h2 class="er-heading">{{ shiftPageTitle }}</h2>
      <p class="er-page-note" v-if="shiftPageNote">{{ shiftPageNote }}</p>
      <table class="er-table er-table--shift-matrix">
        <thead>
          <tr>
            <th class="er-table__metric" rowspan="2">{{ $t('er_col_metric') }}</th>
            <th
              v-for="shift in pageShifts"
              :key="`shift-h-${shift.shiftId}`"
              :colspan="hasComparison ? 3 : 1"
            >
              {{ shift.name }}
            </th>
          </tr>
          <tr>
            <th
              v-for="col in shiftMonthHeaders(pageShifts)"
              :key="col.key"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in shiftMatrixRows(pageShifts)" :key="row.metricKey">
            <td class="er-table__metric">
              <span class="er-table__swatch" :style="{ background: metricColor(row.metricKey) }"></span>
              {{ $t(row.metricKey) }}
            </td>
            <td
              v-for="cell in row.cells"
              :key="cell.key"
              :class="cell.incDecClass"
            >
              {{ cell.text }}
            </td>
          </tr>
        </tbody>
      </table>
      <executive-report-page-footer
        :parts="contactLineParts"
        :copy="footerText"
        :rtl="isRTL"
        :last-page="isLastPage(`shifts-${pageIdx}`)"
      />
    </section>

    <!-- 4 Performance Trends -->
    <section class="er-page er-page--trends" v-bind="pageAttrsFor('trends')" v-if="trendCardCount">
      <h2 class="er-heading">{{ $t('er_page_performance_trends') }}</h2>
      <p class="er-page-note" v-if="singleMonthChartCaption">{{ singleMonthChartCaption }}</p>

      <div class="er-trend-grid" :class="`er-trend-grid--${trendCardCount}`">
        <article class="er-trend-card" v-if="hasKpi('responseTime')">
          <h3>{{ $t('er_page_response_time') }}</h3>
          <div class="er-trend-card__value" :style="{ color: metricColor('er_metric_avg_response_time') }">
            <span dir="ltr">{{ formatDuration(report.metrics.current.avgResponseTimeSeconds) }}</span>
          </div>
          <div class="er-trend-card__meta" v-if="hasComparison">
            {{ $t('er_stat_vs_minutes', { value: formatDuration(report.metrics.previous?.avgResponseTimeSeconds), month: monthFull('comparison') }) }}
          </div>
          <Chart
            type="bar"
            :labels="chartLabels(report.charts.responseTime)"
            :datasets="responseTimeDatasets"
            :options="responseTimeChartOptions"
            :height="trendChartHeight"
            width="100%"
            isPlugin
          />
        </article>

        <article class="er-trend-card" v-if="hasKpi('avgScore')">
          <h3>{{ $t('er_page_avg_score') }}</h3>
          <div class="er-trend-card__value" :style="{ color: metricColor('er_metric_avg_score') }">
            {{ report.metrics.current.avgScore ?? '–' }}
          </div>
          <div class="er-trend-card__meta" v-if="hasComparison">
            {{ $t('er_stat_vs_count', { value: formatCell(report.metrics.previous?.avgScore), month: monthFull('comparison') }) }}
          </div>
          <Chart
            type="line"
            :labels="chartLabels(report.charts.avgScore)"
            :datasets="lineDataset(report.charts.avgScore, metricColor('er_metric_avg_score'))"
            :options="scoreLineOptions"
            :height="trendChartHeight"
            width="100%"
            isPlugin
          />
        </article>

        <article class="er-trend-card" v-if="hasKpi('cleanerArrivals')">
          <h3>{{ $t('er_metric_cleaner_arrivals') }}</h3>
          <div class="er-trend-card__value" :style="{ color: metricColor('er_metric_cleaner_arrivals') }">
            {{ formatCell(report.metrics.current.cleanerArrivals) }}
          </div>
          <div class="er-trend-card__meta" v-if="hasComparison">
            {{ $t('er_stat_vs_count', { value: formatCell(report.metrics.previous?.cleanerArrivals), month: monthFull('comparison') }) }}
          </div>
          <Chart
            type="bar"
            :labels="chartLabels(report.charts.cleanerArrivals)"
            :datasets="barDataset(report.charts.cleanerArrivals, metricColor('er_metric_cleaner_arrivals'))"
            :options="barChartOptions(9, 'arrivals', report.charts.cleanerArrivals)"
            :height="trendChartHeight"
            width="100%"
            isPlugin
          />
        </article>

        <article class="er-trend-card" v-if="hasKpi('complaints')">
          <h3>{{ $t('er_metric_complaints') }}</h3>
          <div class="er-trend-card__value" :style="{ color: metricColor('er_metric_complaints') }">
            {{ formatCell(report.metrics.current.complaints) }}
          </div>
          <div class="er-trend-card__meta" v-if="hasComparison">
            {{ $t('er_stat_vs_count', { value: formatCell(report.metrics.previous?.complaints), month: monthFull('comparison') }) }}
          </div>
          <Chart
            type="bar"
            :labels="chartLabels(report.charts.complaints)"
            :datasets="barDataset(report.charts.complaints, metricColor('er_metric_complaints'))"
            :options="barChartOptions(10, 'complaints', report.charts.complaints)"
            :height="trendChartHeight"
            width="100%"
            isPlugin
          />
        </article>
      </div>
      <executive-report-page-footer
        :parts="contactLineParts"
        :copy="footerText"
        :rtl="isRTL"
        :last-page="isLastPage('trends')"
      />
    </section>

    <!-- 5 Complaints vs Visits -->
    <section class="er-page" v-bind="pageAttrsFor('cv')" v-if="showComplaintsVisitsPage">
      <h2 class="er-heading">{{ $t('er_page_complaints_vs_visits') }}</h2>
      <p class="er-page-note" v-if="singleMonthChartCaption">{{ singleMonthChartCaption }}</p>
      <p class="er-page-note" v-if="hasKpi('complaintsVsVisits')">{{ complaintsVisitsFormula }}</p>

      <div class="er-metric-strip" :class="`er-metric-strip--${cvStatCount}`">
        <div class="er-stat" v-if="hasKpi('complaints')">
          <div class="er-stat__value" :style="{ color: metricColor('er_metric_complaints') }">
            {{ formatCell(report.metrics.current.complaints) }}
          </div>
          <div class="er-stat__label">{{ $t('er_label_complaints') }}</div>
          <div class="er-stat__sub">{{ monthFull('current') }}</div>
        </div>
        <div class="er-stat" v-if="hasKpi('visits')">
          <div class="er-stat__value" :style="{ color: metricColor('er_metric_customer_visits') }">
            {{ formatCell(report.metrics.current.visits) }}
          </div>
          <div class="er-stat__label">{{ $t('er_label_visits') }}</div>
          <div class="er-stat__sub">{{ monthFull('current') }}</div>
        </div>
        <div class="er-stat" v-if="hasKpi('complaintsVsVisits')">
          <div class="er-stat__value" :style="{ color: metricColor('er_metric_complaints_vs_visits_pct') }">
            {{ report.metrics.current.complaintsToVisitRatio ?? '–' }}<span v-if="report.metrics.current.complaintsToVisitRatio != null">%</span>
          </div>
          <div class="er-stat__label">{{ $t('er_label_complaints_per_visits') }}</div>
          <div class="er-stat__sub">{{ complaintsVisitsFraction }}</div>
        </div>
      </div>

      <div class="er-cv-grid">
        <div class="er-chart-panel er-chart-panel--bordered er-cv-wide" v-if="complaintsVisitsDatasets.length">
          <h4>{{ $t('er_page_complaints_vs_visits') }}</h4>
          <Chart
            type="line"
            :labels="chartLabels(report.charts.complaints)"
            :datasets="complaintsVisitsDatasets"
            :options="complaintsVisitsChartOptions"
            height="185px"
            width="100%"
            isPlugin
          />
        </div>
        <div class="er-chart-panel er-chart-panel--bordered er-cv-wide" v-if="hasKpi('complaintsVsVisits')">
          <h4>{{ $t('er_chart_ratio') }}</h4>
          <Chart
            type="line"
            :labels="chartLabels(report.charts.complaintsToVisitRatio)"
            :datasets="ratioDatasets"
            :options="ratioChartOptions"
            height="185px"
            width="100%"
            isPlugin
          />
        </div>
      </div>
      <executive-report-page-footer
        :parts="contactLineParts"
        :copy="footerText"
        :rtl="isRTL"
        :last-page="isLastPage('cv')"
      />
    </section>

    <!-- 6 Complaint Drivers -->
    <section class="er-page" v-bind="pageAttrsFor('drivers')" v-if="showComplaintDrivers">
      <h2 class="er-heading">{{ $t('er_page_complaint_drivers') }}</h2>
      <p class="er-page-note" v-if="complaintTypesNote">{{ complaintTypesNote }}</p>

      <div class="er-types-layout er-types-layout--compact">
        <div class="er-chart-panel er-chart-panel--bordered er-chart-panel--doughnut">
          <Chart
            v-if="printComplaintTypes.length"
            type="doughnut"
            :labels="printComplaintTypes.map((t) => t.name)"
            :datasets="doughnutDataset(printComplaintTypes)"
            :options="doughnutOptions"
            height="220px"
            width="100%"
            isPlugin
          />
        </div>
        <table class="er-table er-table--types">
          <thead>
            <tr><th>{{ $t('er_col_complaint') }}</th><th>%</th><th>{{ $t('er_col_qty') }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in printComplaintTypes" :key="row.name">
              <td class="er-table__name">
                <span class="er-type-name">
                  <span
                    class="er-type-swatch"
                    :style="{ backgroundColor: complaintTypeColors[index] }"
                  ></span>
                  {{ row.name }}
                </span>
              </td>
              <td>{{ row.percent }}%</td>
              <td>{{ formatCell(row.count) }}</td>
            </tr>
            <tr class="er-table__total">
              <td>{{ $t('er_total') }}</td>
              <td></td>
              <td>{{ formatCell(report.metrics.current.complaints) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="er-board-empty" v-if="!hasHotSpots">{{ tPeriod('er_hot_empty') }}</p>

      <div
        class="er-hot-grid er-hot-grid--compact"
        :class="{ 'er-hot-grid--2': !showRatioDrivers }"
        v-else
      >
        <section class="er-hot-card">
          <h3>{{ $t('er_hot_slowest') }}</h3>
          <table class="er-table er-table--hot">
            <thead>
              <tr>
                <th>{{ $t('er_hot_col_room') }}</th>
                <th>{{ $t('er_col_response') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in paddedHotRows(slowestRooms)" :key="`rt-${idx}`">
                <td class="er-table__name">{{ row ? row.name : '' }}</td>
                <td><span dir="ltr" v-if="row">{{ formatDuration(row.avgResponseTimeSeconds) }}</span></td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="er-hot-card" v-if="showRatioDrivers">
          <h3>{{ $t('er_hot_ratio') }}</h3>
          <table class="er-table er-table--hot">
            <thead>
              <tr>
                <th>{{ $t('er_hot_col_room') }}</th>
                <th>{{ $t('er_hot_col_ratio') }}</th>
                <th>{{ $t('er_hot_col_change') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in paddedHotRows(ratioDrivers)" :key="`ratio-${idx}`">
                <td class="er-table__name">{{ row ? row.name : '' }}</td>
                <td>{{ row ? formatCell(row.currentRatio) : '' }}</td>
                <td :class="row ? hotDeltaClass(row.delta) : null">
                  {{ row ? formatHotDelta(row.delta) : '' }}
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="er-hot-card">
          <h3>{{ $t('er_hot_top_type', { name: topComplaintName || '–' }) }}</h3>
          <table class="er-table er-table--hot">
            <thead>
              <tr>
                <th>{{ $t('er_hot_col_room') }}</th>
                <th>{{ $t('er_hot_col_count') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in paddedHotRows(topComplaintRooms)" :key="`type-${idx}`">
                <td class="er-table__name">{{ row ? row.name : '' }}</td>
                <td>{{ row ? formatCell(row.count) : '' }}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>

      <executive-report-page-footer
        :parts="contactLineParts"
        :copy="footerText"
        :rtl="isRTL"
        :last-page="isLastPage('drivers')"
      />
    </section>

    <section class="er-page" v-bind="pageAttrsFor('expiredSlas')" v-if="expiredSlaRooms.length">
      <h2 class="er-heading">{{ $t('er_hot_expired_slas') }}</h2>
      <table class="er-table er-table--hot er-table--expired-slas">
        <thead>
          <tr>
            <th>{{ $t('er_hot_col_room') }}</th>
            <th>{{ $t('er_hot_col_count') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in expiredSlaRooms" :key="`sla-${row.roomId}`">
            <td class="er-table__name">{{ row.name }}</td>
            <td>{{ formatCell(row.count) }}</td>
          </tr>
        </tbody>
      </table>
      <executive-report-page-footer
        :parts="contactLineParts"
        :copy="footerText"
        :rtl="isRTL"
        :last-page="isLastPage('expiredSlas')"
      />
    </section>

    <!-- Rooms -->
    <section
      class="er-page"
      v-for="(pageRooms, pageIdx) in roomTablePages"
      :key="`rooms-${pageIdx}`"
      v-bind="pageAttrsFor(`rooms-${pageIdx}`)"
    >
      <h2 class="er-heading">{{ $t('er_page_rooms') }}</h2>
      <p class="er-page-note" v-if="pageIdx === 0">
        {{ $t('er_rooms_note', { month: monthFull('current') }) }}
      </p>

      <table class="er-table er-table--rooms">
        <thead>
          <tr>
            <th class="er-table__metric">{{ $t('er_hot_col_room') }}</th>
            <th>{{ $t('er_label_visits') }}</th>
            <th>{{ $t('er_label_complaints') }}</th>
            <th>{{ $t('er_col_cleaners') }}</th>
            <th>{{ $t('er_col_response') }}</th>
            <th>{{ $t('er_col_score') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in pageRooms" :key="`room-${row.roomId}`">
            <td class="er-table__name">{{ row.name }}</td>
            <td>{{ formatCell(row.visits) }}</td>
            <td>{{ formatCell(row.complaints) }}</td>
            <td>{{ formatCell(row.cleanerArrivals) }}</td>
            <td><span dir="ltr">{{ formatDuration(row.avgResponseTimeSeconds) }}</span></td>
            <td>{{ formatCell(row.avgScore) }}</td>
          </tr>
        </tbody>
      </table>

      <executive-report-page-footer
        :parts="contactLineParts"
        :copy="footerText"
        :rtl="isRTL"
        :last-page="isLastPage(`rooms-${pageIdx}`)"
      />
    </section>
    </template>
  </section>
</template>

<script>
import ChartJs from 'chart.js'
import Chart from '@/modules/common/components/Chart'
import ExecutiveReportPageFooter from './ExecutiveReportPageFooter'
import { isRtlLocale, normalizeLanguage } from '@/modules/common/services/localeService'
import html2pdf from 'html2pdf.js'
import scssVars from '@/styles/vars.scss'
import { analyticsChartColors, chartColors } from '../consts/consts'
import performanceChartService from '../services/performanceChartService'
import {
  ALL_EXECUTIVE_REPORT_KPI_IDS,
  COMPARISON_ROW_KPI,
  FINDING_KPI,
} from '../consts/executiveReportKpis'

const BUZZZ_LOGO = require('@/assets/buzzztv_logo.png')
const COVER_HERO = require('@/assets/performance/report-cover-hero.jpg')
const COVER_LOGO_MAX_WIDTH = 150
const COVER_LOGO_MAX_HEIGHT = 44
const COVER_SPARK_COLOR = '#0B3B5C'

const FINDING_KPI_IDS = [...new Set(Object.values(FINDING_KPI))]

/**
 * The report reuses the Analytics dashboard's own palette so a metric keeps the same
 * colour in the PDF that the reader just saw on screen.
 */
const METRIC_COLORS = {
  er_metric_avg_score: chartColors.avg_score.line,
  er_metric_avg_response_time: chartColors.response_time_SLA.line,
  er_metric_cleaner_arrivals: chartColors.arrivals.line,
  er_metric_supervisor_inspections: scssVars.jaffa,
  er_metric_expired_slas: chartColors.response_time_SLA.line,
  er_metric_complaints_vs_visits_pct: chartColors.complaints_vs_visits.line,
  er_metric_complaints: chartColors.complaints.line,
  er_metric_customer_visits: chartColors.visits.line,
}
/**
 * The three cover KPIs, each paired with the trend series that feeds its sparkline.
 */
const COVER_HEADLINE_SPECS = [
  { metricKey: 'er_metric_avg_score', unitKey: null, seriesKey: 'avgScore' },
  { metricKey: 'er_metric_avg_response_time', unitKey: null, seriesKey: 'responseTime' },
  {
    metricKey: 'er_metric_complaints_vs_visits_pct',
    unitKey: null,
    unitText: '%',
    seriesKey: 'complaintsToVisitRatio',
  },
]
const SLA_LINE_COLOR = '#dc2626'
const STOCK_COVER_TITLE = 'Performance Review'
const CS_EMAIL = 'shira@buzzztech.com'
const CS_PHONE = '+972 54-918-8813'
const PAGE_PX_WIDTH = 1050
const PAGE_PX_HEIGHT = Math.round((8.5 / 11) * PAGE_PX_WIDTH)
const PRINT_COMPLAINT_TYPE_LIMIT = 8
/** Skip the default 1s Chart.js tween. These canvases are only captured for the PDF. */
const EXPORT_CHART_ANIMATION = { duration: 0 }
/** Charts follow the screen's pixel ratio and zoom by default, so each user got different chart sharpness. */
const EXPORT_CHART_PIXEL_RATIO = 2
/** A background tab runs timers about once a second, so a slow machine gets plenty of time before export fails. */
const CHART_RENDER_TIMEOUT_MS = 20000
/** ~120dpi on letter landscape. 1.5 looked sharper and made each page much slower to rasterize. */
const EXPORT_CANVAS_SCALE = 1.25
/** Month columns that fit across the landscape page next to the metric name column. */
const MONTHS_PER_PAGE = 12
/** The room table has no wrapped header, so it fits a couple more rows than the month table. */
const ROOMS_PER_PAGE = 18

/**
 * @param {string} color rgba()/hex slice fill
 * @returns {{ r: number, g: number, b: number, a: number }}
 */
function parseCssColor(color) {
  const raw = (color || '').trim()
  const hex = raw.replace(/^#/, '')
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: 1,
    }
  }
  if (/^[0-9a-f]{3}$/i.test(hex) && !raw.includes('(')) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
      a: 1,
    }
  }
  const rgba = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/.exec(raw)
  if (rgba) {
    return {
      r: Number(rgba[1]),
      g: Number(rgba[2]),
      b: Number(rgba[3]),
      a: rgba[4] == null ? 1 : Number(rgba[4]),
    }
  }
  return { r: 15, g: 23, b: 42, a: 1 }
}

/**
 * @param {string} color rgba()/hex slice fill
 * @returns {string} label colour readable on that fill
 */
function sliceLabelColor(color) {
  const { r, g, b, a } = parseCssColor(color)
  if (a < 0.6) return '#0f172a'
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.65 ? '#0f172a' : '#fff'
}

export default {
  name: 'ExecutiveReportPrint',
  components: { Chart, ExecutiveReportPageFooter },
  props: {
    report: { type: Object, required: true },
    autoExport: { type: Boolean, default: true },
    selectedKpis: { type: Array, default: () => [...ALL_EXECUTIVE_REPORT_KPI_IDS] },
  },
  data() {
    return {
      buzzzLogo: BUZZZ_LOGO,
      coverHero: COVER_HERO,
      exporting: false,
    }
  },
  computed: {
    locale() {
      return normalizeLanguage(this.report?.meta?.language || this.$i18n?.locale)
    },
    isRTL() {
      return isRtlLocale(this.locale)
    },
    isWeekly() {
      return this.report.meta.grain === 'week'
    },
    isMonthRange() {
      if (this.isWeekly) return false
      const bucket = this.report?.meta?.periodWindow?.current
      if (!bucket) return false
      if ((bucket.monthCount || 1) > 1) return true
      return !!(bucket.fromYear && (bucket.fromYear !== bucket.year || bucket.fromMonth !== bucket.month))
    },
    showAverageColumn() {
      return !this.isWeekly && !this.isMonthRange
    },
    showLifetimeColumn() {
      return !this.isWeekly && !this.isMonthRange && !!this.report?.meta?.lifetimePeriod
    },
    /** Single-month charts draw January through that month, while the headline is the month itself. */
    singleMonthChartCaption() {
      if (this.isWeekly || this.isMonthRange) return ''
      const month = this.monthEnd('current')
      if (!month) return ''
      return this.$t('er_chart_ytd_window', { month })
    },
    showCoverChartCaption() {
      return !!this.singleMonthChartCaption && this.coverHeadlines.some((item) => item.series.length)
    },
    /** Week range on the cover, e.g. "Aug 2 – Aug 8, 2026" in the active locale. */
    periodLabel() {
      return this.monthFull('current')
    },
    /** Export date in the reader's own locale and timezone, not the server's. */
    reportDateLabel() {
      const generatedAt = this.report?.meta?.generatedAt
      const date = generatedAt ? new Date(generatedAt) : new Date()
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(this.locale, { year: 'numeric', month: 'long', day: 'numeric' })
    },
    complaintsVisitsFormula() {
      const current = this.report?.metrics?.current || {}
      return this.$t('er_cv_formula', {
        complaints: this.formatCell(current.complaints),
        visits: this.formatCell(current.visits),
        ratio: current.complaintsToVisitRatio ?? '–',
        month: this.monthFull('current'),
      })
    },
    complaintsVisitsFraction() {
      const current = this.report?.metrics?.current || {}
      return `${this.formatCell(current.complaints)} ÷ ${this.formatCell(current.visits)}`
    },
    printComparisonRows() {
      return (this.report?.comparisonRows || []).filter((row) => this.hasKpi(COMPARISON_ROW_KPI[row.metricKey]))
    },
    monthBreakdownMetrics() {
      const sample = (this.report?.monthBreakdown || [])[0]
      return (sample?.cells || []).filter((cell) => this.hasKpi(COMPARISON_ROW_KPI[cell.metricKey]))
    },
    /** The months are the columns here, so a long range splits across pages sideways. */
    monthBreakdownPages() {
      const months = this.report?.monthBreakdown || []
      if (!months.length || !this.monthBreakdownMetrics.length) return []
      const pages = []
      for (let i = 0; i < months.length; i += MONTHS_PER_PAGE) {
        pages.push(months.slice(i, i + MONTHS_PER_PAGE))
      }
      return pages
    },
    hasComparison() {
      const meta = this.report?.meta
      if (meta?.comparisonEnabled === false) return false
      return !!meta?.periodWindow?.comparison
    },
    comparisonPageTitle() {
      return this.hasComparison ? this.$t('er_page_comparison') : this.tPeriod('er_page_period_summary')
    },
    comparisonPageNote() {
      if (this.isMonthRange) {
        if (this.hasComparison) return ''
        return this.tPeriod('er_period_only_note', { month: this.monthFull('current') })
      }
      if (this.hasComparison) {
        return this.tPeriod('er_comparison_note', {
          month: this.isWeekly ? this.monthFull('comparison') : this.monthEnd('current'),
        })
      }
      return this.tPeriod('er_period_only_note', { month: this.monthFull('current') })
    },
    shiftPageTitle() {
      return this.hasComparison ? this.$t('er_page_shift_comparison') : this.$t('er_page_shift_summary')
    },
    shiftPageNote() {
      if (this.hasComparison) return ''
      return this.tPeriod('er_period_only_note', { month: this.monthShort('current') })
    },
    showFindingsPage() {
      return this.hasComparison && FINDING_KPI_IDS.some((id) => this.hasKpi(id))
    },
    trendCardCount() {
      return ['responseTime', 'avgScore', 'cleanerArrivals', 'complaints'].filter((id) => this.hasKpi(id)).length
    },
    trendChartHeight() {
      return this.trendCardCount > 2 ? '100%' : '245px'
    },
    printShiftComparisons() {
      return (this.report?.shiftComparisons || [])
        .map((shift) => ({
          ...shift,
          rows: (shift.rows || []).filter((row) => this.hasKpi(COMPARISON_ROW_KPI[row.metricKey])),
        }))
        .filter((shift) => shift.rows.length)
    },
    shiftComparisonPages() {
      const pages = []
      const shifts = this.printShiftComparisons
      const perPage = this.hasComparison ? 3 : 5
      for (let i = 0; i < shifts.length; i += perPage) {
        pages.push(shifts.slice(i, i + perPage))
      }
      return pages
    },
    showComplaintsVisitsPage() {
      return ['complaints', 'visits', 'complaintsVsVisits'].some((id) => this.hasKpi(id))
    },
    cvStatCount() {
      return ['complaints', 'visits', 'complaintsVsVisits'].filter((id) => this.hasKpi(id)).length
    },
    complaintTypesNote() {
      return this.sentences(this.report?.narratives?.complaintTypes)
    },
    coverTitle() {
      const suffix = this.report?.branding?.reportTitleSuffix
      if (!suffix || suffix === STOCK_COVER_TITLE) return this.$t('er_cover_default_title')
      return suffix
    },
    coverHeadlines() {
      const rows = this.report?.comparisonRows || []
      const byKey = {}
      rows.forEach((row) => {
        byKey[row.metricKey] = row
      })
      return COVER_HEADLINE_SPECS.map((spec) => {
        if (!this.hasKpi(COMPARISON_ROW_KPI[spec.metricKey])) return null
        const row = byKey[spec.metricKey]
        if (!row || row.current == null) return null
        return {
          ...row,
          metricKey: spec.metricKey,
          unit: spec.unitKey ? this.$t(spec.unitKey) : spec.unitText || '',
          color: COVER_SPARK_COLOR,
          series: this.report?.charts?.[spec.seriesKey] || [],
        }
      }).filter(Boolean)
    },
    roomTablePages() {
      if (!this.hasKpi('roomTable')) return []
      const rows = this.report?.rooms || []
      const pages = []
      for (let i = 0; i < rows.length; i += ROOMS_PER_PAGE) {
        pages.push(rows.slice(i, i + ROOMS_PER_PAGE))
      }
      return pages
    },
    slaMinutes() {
      const minutes = Number(this.report?.meta?.slaMinutes)
      return Number.isFinite(minutes) && minutes > 0 ? minutes : null
    },
    /**
     * Response time bars with the site's SLA target drawn across them, so a bar above
     * the line is a month that missed the target.
     */
    responseTimeDatasets() {
      const series = this.report?.charts?.responseTime || []
      const datasets = this.barDataset(series, this.metricColor('er_metric_avg_response_time'))
      datasets[0].label = this.$t('er_metric_avg_response_time')
      if (this.slaMinutes == null) return datasets
      datasets.push({
        type: 'line',
        label: this.$t('er_chart_sla'),
        data: series.map(() => this.slaMinutes),
        borderColor: SLA_LINE_COLOR,
        backgroundColor: SLA_LINE_COLOR,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        datalabels: { display: false },
      })
      return datasets
    },
    responseTimeChartOptions() {
      const series = this.report?.charts?.responseTime || []
      if (this.slaMinutes == null) return this.barChartOptions(11, 'response_time_SLA', series)
      // The SLA target has to be inside the axis range or the line is clipped off the chart.
      const options = this.barChartOptions(11, 'response_time_SLA', [...series, { value: this.slaMinutes }])
      return { ...options, legend: this.slaChartLegend() }
    },
    complaintsVisitsDatasets() {
      const datasets = []
      if (this.hasKpi('complaints')) {
        datasets.push(
          this.axisLineDataset({
            series: this.report?.charts?.complaints,
            color: this.metricColor('er_metric_complaints'),
            label: this.$t('er_label_complaints'),
            yAxisID: 'complaints',
            labelAlign: 'bottom',
          }),
        )
      }
      if (this.hasKpi('visits')) {
        datasets.push(
          this.axisLineDataset({
            series: this.report?.charts?.visits,
            color: this.metricColor('er_metric_customer_visits'),
            label: this.$t('er_label_visits'),
            yAxisID: 'visits',
            labelAlign: 'top',
          }),
        )
      }
      return datasets
    },
    complaintsVisitsChartOptions() {
      const axes = []
      if (this.hasKpi('complaints')) {
        axes.push(this.valueAxis('complaints', 'left', 'complaints', this.report?.charts?.complaints))
      }
      if (this.hasKpi('visits')) {
        axes.push(
          this.valueAxis('visits', axes.length ? 'right' : 'left', 'visits', this.report?.charts?.visits),
        )
      }
      return this.dualAxisOptions(axes)
    },
    ratioDatasets() {
      return [
        this.axisLineDataset({
          series: this.report?.charts?.complaintsToVisitRatio,
          color: this.metricColor('er_metric_complaints_vs_visits_pct'),
          label: this.$t('er_chart_ratio'),
          yAxisID: 'ratio',
          labelAlign: 'top',
          fractional: true,
        }),
      ]
    },
    ratioChartOptions() {
      return this.dualAxisOptions([
        this.valueAxis('ratio', 'left', 'complaints_vs_visits', this.report?.charts?.complaintsToVisitRatio),
      ])
    },
    coverHighlights() {
      if (!this.hasComparison) return []
      const byId = {}
      ;[...this.successFindings, ...this.riskFindings].forEach((finding) => {
        byId[finding.id] = finding
      })
      return [
        { id: 'avg_score', successKey: 'er_cover_score_rose', riskKey: 'er_cover_score_eased' },
        { id: 'response_time', successKey: 'er_cover_response_improved', riskKey: 'er_cover_response_slowed' },
        { id: 'complaints_ratio', successKey: 'er_cover_ratio_fell', riskKey: 'er_cover_ratio_rose' },
      ]
        .map((spec) => {
          const finding = byId[spec.id]
          if (!finding) return null
          const improved = finding.severity === 'success'
          return {
            id: spec.id,
            text: this.$t(improved ? spec.successKey : spec.riskKey),
            tone: improved ? 'inc-good' : 'inc-bad',
          }
        })
        .filter(Boolean)
    },
    slowestRooms() {
      return this.report?.hotSpots?.slowestRooms || []
    },
    ratioDrivers() {
      return this.report?.hotSpots?.ratioDrivers || []
    },
    showRatioDrivers() {
      return this.hasComparison && this.ratioDrivers.length > 0
    },
    topComplaintRooms() {
      return this.report?.hotSpots?.topComplaint?.rooms || []
    },
    topComplaintName() {
      return this.report?.hotSpots?.topComplaint?.name || ''
    },
    expiredSlaRooms() {
      if (!this.hasKpi('expiredSlas')) return []
      return this.report?.hotSpots?.expiredSlaRooms || []
    },
    showComplaintDrivers() {
      return this.hasKpi('complaintDrivers')
    },
    hasHotSpots() {
      return this.slowestRooms.length > 0 || this.showRatioDrivers || this.topComplaintRooms.length > 0
    },
    hotSpotRowCount() {
      const counts = [this.slowestRooms.length, this.topComplaintRooms.length]
      if (this.showRatioDrivers) counts.push(this.ratioDrivers.length)
      return Math.max(...counts, 1)
    },
    contactLineParts() {
      const emailToken = '[[ER_EMAIL]]'
      const phoneToken = '[[ER_PHONE]]'
      const line = this.$t('er_contact_line', { email: emailToken, phone: phoneToken })
      const parts = []
      const tokenRe = /\[\[ER_EMAIL\]\]|\[\[ER_PHONE\]\]/g
      let lastIndex = 0
      let match = tokenRe.exec(line)
      while (match) {
        if (match.index > lastIndex) {
          parts.push({ text: line.slice(lastIndex, match.index), ltr: false })
        }
        parts.push({
          text: match[0] === emailToken ? CS_EMAIL : CS_PHONE,
          ltr: true,
          href: match[0] === emailToken ? `mailto:${CS_EMAIL}` : null,
        })
        lastIndex = match.index + match[0].length
        match = tokenRe.exec(line)
      }
      if (lastIndex < line.length) {
        parts.push({ text: line.slice(lastIndex), ltr: false })
      }
      return parts
    },
    footerText() {
      return this.$t('er_footer_copyright')
    },
    printPageIds() {
      const ids = ['cover']
      if (this.showFindingsPage) ids.push('findings')
      if (this.printComparisonRows.length) ids.push('comparison')
      this.monthBreakdownPages.forEach((_, idx) => ids.push(`months-${idx}`))
      this.shiftComparisonPages.forEach((_, idx) => ids.push(`shifts-${idx}`))
      if (this.trendCardCount) ids.push('trends')
      if (this.showComplaintsVisitsPage) ids.push('cv')
      if (this.showComplaintDrivers) ids.push('drivers')
      if (this.expiredSlaRooms.length) ids.push('expiredSlas')
      this.roomTablePages.forEach((_, idx) => ids.push(`rooms-${idx}`))
      return ids
    },
    lastPrintPageId() {
      return this.printPageIds[this.printPageIds.length - 1]
    },
    successFindings() {
      return (this.report?.findings || []).filter(
        (f) => f.severity === 'success' && this.hasKpi(FINDING_KPI[f.id]),
      )
    },
    riskFindings() {
      return (this.report?.findings || []).filter(
        (f) => f.severity === 'risk' && this.hasKpi(FINDING_KPI[f.id]),
      )
    },
    findingBoards() {
      return [
        {
          severity: 'success',
          items: this.successFindings,
          pillKey: 'er_verdict_improved',
          titleKey: 'er_board_working',
        },
        {
          severity: 'risk',
          items: this.riskFindings,
          pillKey: 'er_verdict_watch',
          titleKey: 'er_board_attention',
        },
      ].filter((board) => board.items.length)
    },
    printComplaintTypes() {
      return (this.report?.complaintTypes || []).slice(0, PRINT_COMPLAINT_TYPE_LIMIT)
    },
    complaintTypeColors() {
      return this.printComplaintTypes.map((_, index) => analyticsChartColors.cleaning[index])
    },
    doughnutOptions() {
      return {
        animation: EXPORT_CHART_ANIMATION,
        devicePixelRatio: EXPORT_CHART_PIXEL_RATIO,
        legend: { display: false },
        maintainAspectRatio: false,
        cutoutPercentage: 42,
        layout: { padding: 18 },
        plugins: {
          datalabels: {
            // Small slices stay in the table; labelling them on the pie just collides.
            display: (ctx) => {
              const value = Number(ctx.dataset.data[ctx.dataIndex])
              return Number.isFinite(value) && value >= 8 ? 'auto' : false
            },
            color: (ctx) => sliceLabelColor(this.complaintTypeColors[ctx.dataIndex]),
            font: { weight: '700', size: 14 },
            formatter: (value) => {
              const n = Math.round(Number(value))
              return Number.isFinite(n) ? `${n}%` : ''
            },
          },
        },
      }
    },
    scoreLineOptions() {
      return {
        animation: EXPORT_CHART_ANIMATION,
        devicePixelRatio: EXPORT_CHART_PIXEL_RATIO,
        legend: { display: false },
        layout: { padding: { top: 18, right: 8 } },
        scales: {
          yAxes: [
            {
              ticks: {
                ...this.spacedAxisTicks(this.panelAxisTicks('avg_score', this.report?.charts?.avgScore)),
                precision: 0,
              },
              gridLines: { color: 'rgba(15, 23, 42, 0.08)' },
            },
          ],
          xAxes: [this.monthAxis()],
        },
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            align: 'top',
            anchor: 'end',
            clamp: true,
            color: '#0f172a',
            font: { weight: '600', size: 11 },
            formatter: (value) => {
              if (value == null || value === '') return ''
              const n = Number(value)
              return Number.isFinite(n) ? Math.round(n) : ''
            },
          },
        },
      }
    },
  },
  mounted() {
    this.$emit('progress', { stage: 'layout' })
    if (this.autoExport) this.exportPdf()
  },
  methods: {
    hasKpi(id) {
      return !!id && Array.isArray(this.selectedKpis) && this.selectedKpis.includes(id)
    },
    isLastPage(id) {
      return this.lastPrintPageId === id
    },
    /**
     * The exporter clones one `.er-page` at a time into a bare container, so the
     * page itself must carry direction, theme, and last-page state.
     */
    pageAttrsFor(id) {
      const accent = this.report?.branding?.accentColor || '#0B3B5C'
      return {
        dir: this.isRTL ? 'rtl' : 'ltr',
        class: {
          'is-rtl': this.isRTL,
          'is-last-page': this.isLastPage(id),
        },
        style: {
          '--er-accent': accent,
          '--er-accent-2': this.report?.branding?.accentColorSecondary || '#D4A017',
        },
      }
    },
    metricColor(metricKey) {
      const color = METRIC_COLORS[metricKey]
      if (!color) throw new Error(`Executive report has no panel colour for metric "${metricKey}"`)
      return color
    },
    /**
     * Month labels under a half-width trend card. A tilted "Sep 25" is wider than the
     * column, so the next month paints over it. The name and year stack, and a label
     * that still cannot fit is left off rather than drawn on top of its neighbour.
     */
    monthAxis() {
      return {
        offset: true,
        ticks: {
          autoSkip: true,
          autoSkipPadding: 6,
          maxRotation: 0,
          minRotation: 0,
          fontSize: 10,
          padding: 2,
          callback: (value) => this.monthAxisLabel(value),
        },
        gridLines: { display: false },
      }
    },
    monthAxisLabel(value) {
      const label = value == null ? '' : String(value)
      const split = /^(.*\S)\s+(\d{2})$/.exec(label)
      if (!split) return label
      return [split[1], split[2]]
    },
    /**
     * Chart.js prints an explicit min and max as their own ticks, then also prints the
     * round step next to them, so 18 lands on 20 and 2,089 lands on 2,000. Suggest the
     * range instead and let the scale keep one evenly spaced set.
     */
    spacedAxisTicks(ticks) {
      const next = { ...ticks, maxTicksLimit: 6, fontSize: 10, padding: 6 }
      if (Number.isFinite(ticks.min)) {
        next.suggestedMin = ticks.min
        delete next.min
      }
      if (Number.isFinite(ticks.max)) {
        next.suggestedMax = ticks.max
        delete next.max
      }
      return next
    },
    /**
     * Monthly bar chart with a value on every column.
     * @param {number} labelSize point-label size; drop it for wide numbers in narrow cards.
     */
    barChartOptions(labelSize = 11, panelLabel = null, series = null) {
      return {
        animation: EXPORT_CHART_ANIMATION,
        devicePixelRatio: EXPORT_CHART_PIXEL_RATIO,
        legend: { display: false },
        layout: { padding: { top: 18, right: 8 } },
        scales: {
          yAxes: [
            {
              ticks: panelLabel
                ? this.spacedAxisTicks(this.panelAxisTicks(panelLabel, series))
                : { beginAtZero: true, maxTicksLimit: 6, fontSize: 10, padding: 6 },
              gridLines: { color: 'rgba(15, 23, 42, 0.08)' },
            },
          ],
          xAxes: [this.monthAxis()],
        },
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            align: 'top',
            anchor: 'end',
            clamp: true,
            color: '#0f172a',
            font: { weight: '600', size: labelSize },
            formatter: (value) => {
              if (value == null || value === '') return ''
              const n = Number(value)
              return Number.isFinite(n) ? Math.round(n) : ''
            },
          },
        },
      }
    },
    /**
     * Month names come from the browser's Intl data for the active locale, so the report
     * never depends on the English labels the API also happens to send.
     */
    tPeriod(key, params) {
      const rangeKey = `${key}_range`
      if (this.isMonthRange && this.$te(rangeKey)) return this.$t(rangeKey, params)
      const weekKey = `${key}_week`
      if (this.isWeekly && this.$te(weekKey)) return this.$t(weekKey, params)
      return this.$t(key, params)
    },
    /** End month of a window. Year-to-date runs through this month, not the whole selected range. */
    monthEnd(key) {
      const bucket = this.report?.meta?.periodWindow?.[key]
      if (!bucket) return ''
      if (this.isWeekly) return this.formatWeekRange(bucket.timeFrom, bucket.timeTo)
      return this.formatMonth(bucket.month, bucket.year, { month: 'long', year: 'numeric' })
    },
    formatMonth(month, year, options) {
      if (!Number.isFinite(Number(month))) return ''
      const date = new Date(Date.UTC(Number(year) || 2000, Number(month) - 1, 1))
      return date.toLocaleDateString(this.locale, { ...options, timeZone: 'UTC' })
    },
    /** Left-to-right block. A year kept inside a Hebrew date otherwise jumps next to the dash. */
    isolateDate(value) {
      return `\u2066${value}\u2069`
    },
    /** Right-to-left block, so a Hebrew day stays on the right of its month. */
    rtlDate(value) {
      return `\u2067${value}\u2069`
    },
    dateRange(start, end) {
      return `${this.isolateDate(start)} – ${this.isolateDate(end)}`
    },
    formatWeekRange(timeFrom, timeTo) {
      const start = new Date(`${timeFrom}T00:00:00Z`)
      const end = new Date(`${timeTo}T00:00:00Z`)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ''
      const dayMonth = { month: 'short', day: 'numeric', timeZone: 'UTC' }
      const startLabel = start.toLocaleDateString(this.locale, dayMonth)
      const endLabel = end.toLocaleDateString(this.locale, dayMonth)
      if (!this.isRTL) {
        const endWithYear = end.toLocaleDateString(this.locale, { ...dayMonth, year: 'numeric' })
        const startWithYear = start.getUTCFullYear() === end.getUTCFullYear()
          ? startLabel
          : start.toLocaleDateString(this.locale, { ...dayMonth, year: 'numeric' })
        return this.dateRange(startWithYear, endWithYear)
      }
      const startYear = String(start.getUTCFullYear())
      const endYear = String(end.getUTCFullYear())
      if (startYear !== endYear) {
        return `${this.rtlDate(startLabel)} ${this.isolateDate(startYear)} – ${this.rtlDate(endLabel)} ${this.isolateDate(endYear)}`
      }
      return `${this.rtlDate(startLabel)} – ${this.rtlDate(endLabel)} ${this.isolateDate(endYear)}`
    },
    monthFull(key) {
      return this.monthLabel(key, 'long', 'numeric')
    },
    monthShort(key) {
      return this.monthLabel(key, 'short', '2-digit')
    },
    monthLabel(key, monthStyle, yearStyle) {
      const bucket = this.report?.meta?.periodWindow?.[key]
      if (!bucket) return ''
      if (this.isWeekly) {
        return this.formatWeekRange(bucket.timeFrom, bucket.timeTo)
      }
      const end = this.formatMonth(bucket.month, bucket.year, { month: monthStyle, year: yearStyle })
      const spansMonths = bucket.fromYear && (bucket.fromYear !== bucket.year || bucket.fromMonth !== bucket.month)
      if (!spansMonths) return end
      const start = this.formatMonth(bucket.fromMonth, bucket.fromYear, { month: monthStyle, year: yearStyle })
      return this.dateRange(start, end)
    },
    formatChartDay(day) {
      const date = new Date(`${String(day).slice(0, 10)}T00:00:00Z`)
      if (Number.isNaN(date.getTime())) return ''
      return this.isolateDate(date.toLocaleDateString(this.locale, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }))
    },
    /** Translate and join an ordered list of `{ key, params }` sentences from the API. */
    sentences(parts) {
      if (!Array.isArray(parts) || !parts.length) return ''
      return parts.map((part) => this.sentence(part)).filter(Boolean).join(' ')
    },
    sentence(part) {
      if (!part || !part.key) return ''
      return this.$t(part.key, {
        month: this.monthFull('current'),
        endMonth: this.monthEnd('current'),
        previousMonth: this.monthFull('comparison'),
        ...(part.params || {}),
      })
    },
    findingDetails(item) {
      return (item && item.sentences ? item.sentences : []).slice(1)
    },
    chartLabels(series) {
      const years = new Set((series || []).map((point) => point.year).filter((year) => year != null))
      const spansYears = years.size > 1
      return (series || []).map((s) => {
        if (s.day) return this.formatChartDay(s.day)
        return this.formatMonth(s.month, s.year, {
          month: 'short',
          year: spansYears ? '2-digit' : undefined,
        })
      })
    },
    barDataset(series, color) {
      return [
        {
          data: (series || []).map((s) => (s.value == null ? null : s.value)),
          backgroundColor: color,
          borderColor: color,
          borderWidth: 1,
        },
      ]
    },
    /**
     * Cover sparklines. A curved line on a strip this short bends past the points and
     * reads as a scribble, so the segments stay straight and the scale hugs the data.
     */
    sparkOptionsFor(series) {
      const values = (series || [])
        .map((point) => (point && point.value != null ? Number(point.value) : null))
        .filter((value) => Number.isFinite(value))
      const ticks = { display: false }
      if (values.length) {
        const min = Math.min(...values)
        const max = Math.max(...values)
        const pad = min === max ? Math.max(Math.abs(min) * 0.08, 1) : (max - min) * 0.18
        ticks.min = min - pad
        ticks.max = max + pad
      }
      return {
        animation: EXPORT_CHART_ANIMATION,
        devicePixelRatio: EXPORT_CHART_PIXEL_RATIO,
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: { enabled: false },
        // Room for the end-point marker (3.5 radius + 2 border) so it is not clipped at the edges.
        layout: { padding: { top: 6, right: 7, bottom: 6, left: 7 } },
        elements: {
          line: { tension: 0, borderCapStyle: 'round', borderJoinStyle: 'round' },
        },
        scales: {
          yAxes: [{ display: false, ticks, gridLines: { display: false, drawBorder: false } }],
          xAxes: [{ display: false, gridLines: { display: false, drawBorder: false } }],
        },
      }
    },
    sparkDataset(series, color) {
      const data = (series || []).map((point) => (point.value == null ? null : point.value))
      const lastIndex = data.reduce((found, value, index) => (value == null ? found : index), -1)
      return [
        {
          data,
          borderColor: color,
          backgroundColor: 'rgba(11, 59, 92, 0.10)',
          borderWidth: 2,
          pointRadius: data.map((_, index) => (index === lastIndex ? 3.5 : 0)),
          pointHoverRadius: 0,
          pointBackgroundColor: '#fff',
          pointBorderColor: color,
          pointBorderWidth: 2,
          fill: 'start',
          lineTension: 0,
          // Days with no value (e.g. no closed complaints) would otherwise cut the line into dots.
          spanGaps: true,
        },
      ]
    },
    lineDataset(series, color) {
      return [
        {
          data: (series || []).map((s) => (s.value == null ? null : s.value)),
          borderColor: color,
          backgroundColor: color,
          borderWidth: 3,
          pointRadius: 5,
          pointHoverRadius: 6,
          pointBackgroundColor: '#fff',
          pointBorderColor: color,
          pointBorderWidth: 2,
          fill: false,
          lineTension: 0.25,
          spanGaps: true,
        },
      ]
    },
    /**
     * Same y-axis limits as the performance chart for this metric.
     * The panel plots complaints/visits as a fraction; this report plots that ratio as a percent.
     */
    panelAxisTicks(label, series) {
      const values = (series || [])
        .map((point) => (point && point.value != null ? Number(point.value) : null))
        .filter((value) => Number.isFinite(value))
      if (!values.length && label !== 'avg_score') return { beginAtZero: true }
      const axisValues = label === 'complaints_vs_visits' ? values.map((value) => value / 100) : values
      const minMax = performanceChartService.panelAxisMinMax(label, axisValues)
      if (!Number.isFinite(minMax.min) || !Number.isFinite(minMax.max)) return { beginAtZero: true }
      if (label === 'complaints_vs_visits') {
        return { min: minMax.min * 100, max: minMax.max * 100, beginAtZero: false }
      }
      return { min: minMax.min, max: minMax.max, beginAtZero: false }
    },
    chartLegend() {
      return {
        display: true,
        position: 'bottom',
        labels: { boxWidth: 14, fontSize: 10, padding: 10, usePointStyle: false },
      }
    },
    /**
     * The SLA line is dashed on the chart. The legend copies that dash onto a 14px box,
     * and a 6px dash cannot finish a side that short, so the mark breaks into chunks.
     * The swatch is drawn solid; the line on the chart stays dashed.
     */
    slaChartLegend() {
      return {
        ...this.chartLegend(),
        labels: {
          ...this.chartLegend().labels,
          generateLabels(chart) {
            const datasets = chart.data.datasets || []
            return chart._getSortedDatasetMetas().map((meta) => {
              const dataset = datasets[meta.index] || {}
              const dashed = Array.isArray(dataset.borderDash) && dataset.borderDash.length > 0
              return {
                text: dataset.label,
                fillStyle: dataset.backgroundColor,
                strokeStyle: dataset.borderColor,
                lineDash: [],
                lineWidth: dashed ? 0 : dataset.borderWidth,
                hidden: !chart.isDatasetVisible(meta.index),
                datasetIndex: meta.index,
              }
            })
          },
        },
      }
    },
    /**
     * One axis of a two-metric chart. Each metric keeps the dashboard's own scale, so
     * complaints stay readable next to visits that are three orders of magnitude larger.
     */
    valueAxis(id, position, panelLabel, series) {
      return {
        id,
        position,
        ticks: {
          ...this.roundAxisBounds(this.panelAxisTicks(panelLabel, series)),
          maxTicksLimit: 6,
          fontSize: 9,
          padding: 8,
        },
        gridLines: { color: 'rgba(15, 23, 42, 0.08)', drawOnChartArea: position === 'left' },
      }
    },
    /**
     * The panel pads its axis by a ratio, which lands on values like 128430. Chart.js
     * prints that as a tick of its own right next to the round ones, so the corner of
     * the axis reads as two overlapping numbers. Snap the ends to a round step instead.
     */
    roundAxisBounds(ticks) {
      const { min, max } = ticks
      if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return ticks
      const step = Math.pow(10, Math.floor(Math.log10(max - min)) - 1)
      if (!Number.isFinite(step) || step <= 0) return ticks
      const snap = (value, round) => Number((round(value / step) * step).toPrecision(12))
      return { ...ticks, min: snap(min, Math.floor), max: snap(max, Math.ceil) }
    },
    dualAxisOptions(yAxes) {
      return {
        animation: EXPORT_CHART_ANIMATION,
        devicePixelRatio: EXPORT_CHART_PIXEL_RATIO,
        legend: this.chartLegend(),
        layout: { padding: { top: 18, bottom: 8, right: 8, left: 4 } },
        scales: {
          yAxes,
          xAxes: [
            {
              // Half a month of space at each end, so the line and its numbers stop
              // short of the axis labels instead of painting over them.
              offset: true,
              ticks: { autoSkip: false },
              gridLines: { display: false },
            },
          ],
        },
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            // Every point is labelled. Labels sit off the line on a white plate so a
            // crossing line cannot cover the digits.
            anchor: 'center',
            offset: 14,
            clamp: true,
            display: true,
            color: (ctx) => ctx.dataset.borderColor,
            backgroundColor: '#ffffff',
            borderRadius: 3,
            padding: { top: 2, bottom: 2, left: 4, right: 4 },
            font: { weight: '700', size: 10 },
            formatter: (value, ctx) => {
              if (value == null || value === '') return ''
              const n = Number(value)
              if (!Number.isFinite(n)) return ''
              if (ctx.dataset.yAxisID === 'ratio') return `${n.toFixed(2)}%`
              return n.toLocaleString(this.locale)
            },
          },
        },
      }
    },
    /**
     * @param {'top'|'bottom'} labelAlign which side of the line this series labels sit on.
     * @param {boolean} [fractional] The chart component rounds numeric data to one decimal.
     * A ratio like 0.414 needs its digits, so it travels as a string and is left alone.
     */
    axisLineDataset({ series, color, label, yAxisID, labelAlign, fractional = false }) {
      const [dataset] = this.lineDataset(series, color)
      const data = fractional
        ? dataset.data.map((value) => (value == null ? null : String(value)))
        : dataset.data
      return {
        ...dataset,
        data,
        label,
        yAxisID,
        borderWidth: 2.5,
        pointRadius: 3,
        datalabels: { align: this.outerLabelAlign(labelAlign), display: true },
      }
    },
    /**
     * Each number sits on the outer side of its own dot. Complaints and visits
     * use different scales, so a complaint dot can sit above a much larger visit
     * dot. A fixed side then draws each number on the other series.
     * @param {'top'|'bottom'} fallback
     */
    outerLabelAlign(fallback) {
      return (ctx) => {
        const chart = ctx.chart
        const ownY = this.axisPixel(chart, ctx.dataset, ctx.dataIndex)
        if (!Number.isFinite(ownY)) return fallback
        let otherY = null
        chart.data.datasets.forEach((dataset, index) => {
          if (index === ctx.datasetIndex || !chart.isDatasetVisible(index)) return
          const y = this.axisPixel(chart, dataset, ctx.dataIndex)
          if (Number.isFinite(y)) otherY = y
        })
        if (!Number.isFinite(otherY) || otherY === ownY) return fallback
        return ownY < otherY ? 'top' : 'bottom'
      }
    },
    /**
     * Pixel position on this dataset's own scale. The other series is not laid out
     * yet when the first series labels itself, so the point model cannot be read.
     */
    axisPixel(chart, dataset, index) {
      const scale = chart.scales && chart.scales[dataset.yAxisID]
      const raw = dataset.data[index]
      const value = Number(raw)
      if (!scale || raw == null || raw === '' || !Number.isFinite(value)) return null
      return scale.getPixelForValue(value)
    },
    formatDuration(seconds) {
      if (seconds === null || seconds === undefined) return '–'
      const total = Math.round(Number(seconds))
      if (!Number.isFinite(total) || total < 0) return '–'
      const hours = Math.floor(total / 3600)
      const minutes = Math.floor((total % 3600) / 60)
      return [hours, minutes, total % 60].map((part) => String(part).padStart(2, '0')).join(':')
    },
    formatValue(value, format) {
      if (format === 'duration') return this.formatDuration(value)
      return this.formatCell(value)
    },
    doughnutDataset(types) {
      const total = types.reduce((s, t) => s + t.count, 0) || 1
      return [
        {
          data: types.map((t) => Math.round((t.count / total) * 100)),
          backgroundColor: this.complaintTypeColors,
          borderColor: '#fff',
          borderWidth: 2,
        },
      ]
    },
    monthKey(month) {
      return `${month.year}-${month.month}`
    },
    monthBreakdownLabel(month) {
      return this.formatMonth(month.month, month.year, { month: 'short', year: '2-digit' })
    },
    monthCell(month, metricKey) {
      const cell = (month.cells || []).find((item) => item.metricKey === metricKey)
      return this.formatValue(cell ? cell.value : null, cell ? cell.format : null)
    },
    formatCell(value) {
      if (value === null || value === undefined || value === '–' || value === '—') return '–'
      if (typeof value === 'number') return value.toLocaleString(this.locale)
      return value
    },
    incDecClass(row) {
      if (!row || !row.direction || row.incDec === '–' || row.incDec === '—') return ''
      const good =
        (row.higherIsBetter && row.direction === 'up') || (!row.higherIsBetter && row.direction === 'down')
      const bad =
        (row.higherIsBetter && row.direction === 'down') || (!row.higherIsBetter && row.direction === 'up')
      if (good) return 'inc-good'
      if (bad) return 'inc-bad'
      return ''
    },
    shiftMonthHeaders(pageShifts) {
      const current = this.monthShort('current')
      const previous = this.hasComparison ? this.monthShort('comparison') : ''
      const incDec = this.hasComparison ? this.$t('er_col_inc_dec') : ''
      const headers = []
      pageShifts.forEach((shift) => {
        if (this.hasComparison) {
          headers.push({ key: `${shift.shiftId}-prev`, label: previous })
        }
        headers.push({ key: `${shift.shiftId}-cur`, label: current })
        if (this.hasComparison) {
          headers.push({ key: `${shift.shiftId}-inc`, label: incDec })
        }
      })
      return headers
    },
    shiftMatrixRows(pageShifts) {
      const metricKeys = (pageShifts[0] && pageShifts[0].rows ? pageShifts[0].rows : []).map(
        (row) => row.metricKey,
      )
      return metricKeys.map((metricKey) => {
        const cells = []
        pageShifts.forEach((shift) => {
          const row = (shift.rows || []).find((item) => item.metricKey === metricKey) || {}
          if (this.hasComparison) {
            cells.push({
              key: `${shift.shiftId}-${metricKey}-prev`,
              text: this.formatValue(row.previous, row.format),
              incDecClass: '',
            })
          }
          cells.push({
            key: `${shift.shiftId}-${metricKey}-cur`,
            text: this.formatValue(row.current, row.format),
            incDecClass: '',
          })
          if (this.hasComparison) {
            cells.push({
              key: `${shift.shiftId}-${metricKey}-inc`,
              text: row.incDec || '–',
              incDecClass: this.incDecClass(row),
            })
          }
        })
        return { metricKey, cells }
      })
    },
    formatHotDelta(delta) {
      if (delta == null || !Number.isFinite(Number(delta))) return '–'
      const value = Number(delta)
      const sign = value > 0 ? '+' : ''
      return `${sign}${value}`
    },
    paddedHotRows(rows) {
      const list = Array.isArray(rows) ? rows : []
      const padded = list.slice()
      while (padded.length < this.hotSpotRowCount) padded.push(null)
      return padded
    },
    hotDeltaClass(delta) {
      if (delta == null || !Number.isFinite(Number(delta)) || Number(delta) === 0) return ''
      return Number(delta) > 0 ? 'inc-bad' : 'inc-good'
    },
    buildFilename() {
      const company = this.report.meta.companyName.replace(/[/\\:*?"<>|]/g, '-')
      const site = this.report.meta.siteName.replace(/[/\\:*?"<>|]/g, '-')
      const from = this.report.meta.selectedPeriod.timeFrom
      const to = this.report.meta.selectedPeriod.timeTo
      return `${company}-${site}-executive-${from}-to-${to}.pdf`
    },
    waitFrames() {
      return this.$nextTick().then(
        () =>
          new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => resolve())
            })
          }),
      )
    },
    /**
     * html2canvas downloads every stylesheet again inside its capture frame. A tab
     * opened before a deploy asks for a CSS file the new build removed and captures
     * an unstyled page, so each copy gets the rules this page already loaded.
     */
    loadedStylesheets() {
      const byHref = new Map()
      Array.from(document.styleSheets).forEach((sheet) => {
        if (!sheet.href || new URL(sheet.href).origin !== window.location.origin) return
        byHref.set(sheet.href, Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n'))
      })
      return byHref
    },
    inlineStylesheets(doc, byHref) {
      doc.querySelectorAll('link[rel~="stylesheet"]').forEach((link) => {
        const css = byHref.get(link.href)
        if (css == null) return
        const style = doc.createElement('style')
        style.textContent = css
        link.replaceWith(style)
      })
    },
    /** `--er-styles-applied` is declared on `.er-page` only to prove the scoped rules reached the capture copy. */
    assertCaptureStyled(doc) {
      const page = doc.querySelector('.html2pdf__container .er-page')
      if (!page) throw this.renderFailure('capture copy has no .er-page')
      const marker = doc.defaultView.getComputedStyle(page).getPropertyValue('--er-styles-applied').trim()
      if (marker !== '1') throw this.renderFailure('report styles are missing in the capture copy')
    },
    chartName(canvas) {
      const panel = canvas.closest('.er-trend-card, .er-chart-panel, .er-cover__headline')
      const title = panel && panel.querySelector('h3, h4, .er-cover__headline-label')
      return title ? title.textContent.trim() : 'unnamed chart'
    },
    isChartDrawn(canvas) {
      return Object.values(ChartJs.instances).some((chart) => chart.canvas === canvas)
    },
    /** Chart.js draws synchronously when a chart is created, so a live instance on the canvas means it is painted. */
    async waitForCharts(root) {
      const canvases = Array.from(root.querySelectorAll('.er-page canvas'))
      const deadline = Date.now() + CHART_RENDER_TIMEOUT_MS
      let pending = canvases.filter((canvas) => !this.isChartDrawn(canvas))
      while (pending.length) {
        if (Date.now() > deadline) {
          throw this.renderFailure(`charts not drawn after ${CHART_RENDER_TIMEOUT_MS}ms: ${pending.map(this.chartName).join(', ')}`)
        }
        await new Promise((resolve) => setTimeout(resolve, 100))
        pending = canvases.filter((canvas) => !this.isChartDrawn(canvas))
      }
      const empty = canvases.filter((canvas) => !canvas.width || !canvas.height)
      if (empty.length) throw this.renderFailure(`charts have no size: ${empty.map(this.chartName).join(', ')}`)
    },
    /**
     * The page is captured as a picture, so the address would not be clickable.
     * The link is stamped on the last PDF page, over that picture.
     */
    addMailtoLink(pdf, page) {
      const link = page.querySelector('a[href^="mailto:"]')
      if (!(link instanceof HTMLAnchorElement)) return
      const pageRect = page.getBoundingClientRect()
      const linkRect = link.getBoundingClientRect()
      if (!pageRect.width || !pageRect.height || !linkRect.width || !linkRect.height) return
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const x = ((linkRect.left - pageRect.left) / pageRect.width) * pageWidth
      const y = ((linkRect.top - pageRect.top) / pageRect.height) * pageHeight
      const w = (linkRect.width / pageRect.width) * pageWidth
      const h = (linkRect.height / pageRect.height) * pageHeight
      pdf.setPage(pdf.internal.getNumberOfPages())
      pdf.link(x, y, w, h, { url: link.href })
    },
    renderFailure(detail) {
      const err = new Error(this.$t('er_export_render_failed'))
      err.detail = `${detail} | ${navigator.userAgent} | devicePixelRatio ${window.devicePixelRatio}`
      return err
    },
    waitForImage(img, timeoutMs) {
      if (img.complete) return Promise.resolve()
      return new Promise((resolve) => {
        let settled = false
        const done = () => {
          if (settled) return
          settled = true
          clearTimeout(timer)
          resolve()
        }
        const timer = setTimeout(done, timeoutMs)
        img.addEventListener('load', done, { once: true })
        img.addEventListener('error', done, { once: true })
      })
    },
    /**
     * html2canvas ignores object-fit and clips a logo that is larger than the slot,
     * and it drops an image that is still loading. Fit the mark into the slot and
     * replace it with a PNG of that exact size before the page is captured.
     */
    async prepareCoverLogo(root) {
      const img = root.querySelector('.er-cover__logo')
      if (!(img instanceof HTMLImageElement)) return
      await this.waitForImage(img, 2500)
      const naturalWidth = img.naturalWidth
      const naturalHeight = img.naturalHeight
      if (!naturalWidth || !naturalHeight) return
      const scale = Math.min(COVER_LOGO_MAX_WIDTH / naturalWidth, COVER_LOGO_MAX_HEIGHT / naturalHeight)
      const width = Math.max(1, Math.round(naturalWidth * scale))
      const height = Math.max(1, Math.round(naturalHeight * scale))
      try {
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('cover logo canvas missing')
        ctx.drawImage(img, 0, 0, width, height)
        const png = canvas.toDataURL('image/png')
        await new Promise((resolve, reject) => {
          img.onload = () => resolve()
          img.onerror = () => reject(new Error('cover logo png failed to load'))
          img.src = png
        })
      } catch (err) {
        console.error('[ExecutiveReportPrint] cover logo raster failed', err)
      }
      img.style.width = `${width}px`
      img.style.height = `${height}px`
      img.style.maxWidth = 'none'
      img.style.maxHeight = 'none'
      img.style.objectFit = 'fill'
    },
    reportElement() {
      const ref = this.$refs.reportRoot
      if (ref && ref.nodeType === 1) return ref
      const root = this.$el
      if (root && root.nodeType === 1 && root.classList.contains('executive-report-root')) return root
      return null
    },
    async exportPdf() {
      if (this.exporting) return
      this.exporting = true
      let exportError = null
      try {
        await this.$nextTick()
        const el = this.reportElement()
        if (!el) throw new Error('Executive report DOM missing')

        // Leave the report off-screen. html2pdf clones each .er-page into its own
        // capture node, so the dashboard does not have to be covered while it runs.
        // Moving this root onto the viewport made scrollHeight 0 on the next patch.
        void el.offsetHeight
        await this.prepareCoverLogo(el)
        await this.waitForCharts(el)
        await document.fonts.ready
        await this.waitFrames()

        const filename = this.buildFilename()
        const pages = Array.from(el.querySelectorAll('.er-page'))
        if (!pages.length) throw new Error('Executive report has no pages to export')
        pages.forEach((page) => {
          page.style.width = `${PAGE_PX_WIDTH}px`
          page.style.height = `${PAGE_PX_HEIGHT}px`
        })

        const total = pages.length
        this.$emit('progress', { stage: 'capture', page: 1, total })
        await this.waitFrames()
        const stylesheets = this.loadedStylesheets()

        const baseOpts = {
          margin: 0,
          filename,
          image: { type: 'jpeg', quality: 0.9 },
          enableLinks: false,
          jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' },
          html2canvas: {
            scale: EXPORT_CANVAS_SCALE,
            useCORS: true,
            logging: false,
            imageTimeout: 4000,
            scrollX: 0,
            scrollY: 0,
            backgroundColor: '#ffffff',
            width: PAGE_PX_WIDTH,
            height: PAGE_PX_HEIGHT,
            windowWidth: PAGE_PX_WIDTH,
            windowHeight: PAGE_PX_HEIGHT,
            onclone: (doc) => {
              this.inlineStylesheets(doc, stylesheets)
              this.assertCaptureStyled(doc)
            },
          },
        }

        // One DOM page = one PDF page. Avoid html2pdf page-break modes: they
        // insert a blank sheet when the last page is slightly taller than letter.
        let worker = html2pdf().set(baseOpts).from(pages[0]).toPdf()
        for (let i = 1; i < pages.length; i++) {
          const pageNumber = i + 1
          worker = worker
            .get('pdf')
            .then(
              (pdf) =>
                new Promise((resolve) => {
                  this.$emit('progress', { stage: 'capture', page: pageNumber, total })
                  pdf.addPage('letter', 'landscape')
                  requestAnimationFrame(() => resolve(pdf))
                }),
            )
            .set(baseOpts)
            .from(pages[i])
            .toContainer()
            .toCanvas()
            .toPdf()
        }
        await worker
          .get('pdf')
          .then((pdf) => {
            this.addMailtoLink(pdf, pages[pages.length - 1])
            return pdf
          })
          .save(filename)
      } catch (err) {
        exportError = err
        console.error('[ExecutiveReportPrint] export failed', err.detail || '', err)
      } finally {
        this.exporting = false
      }
      // Parent destroys this component on done/error; emit only after restore.
      if (exportError) this.$emit('error', exportError)
      else this.$emit('done')
    },
  },
}
</script>

<style lang="scss" scoped>
.executive-report-root {
  width: 1050px;
  background: #fff;
  color: #0f172a;
  font-family: Arial, Helvetica, sans-serif;
}

.er-page {
  --er-styles-applied: 1;
  position: relative;
  width: 1050px;
  height: 811px;
  max-height: 811px;
  overflow: hidden;
  padding: 28px 36px 58px;
  box-sizing: border-box;
  direction: ltr;
  text-align: left;

  &--trends {
    display: flex;
    flex-direction: column;
  }

  // html2canvas reads only left/right/center/justify, so every direction-aware
  // alignment is spelled out physically instead of with start/end.
  &.is-rtl {
    direction: rtl;
    text-align: right;

    .er-cover__client {
      text-align: left;
    }

    .er-table td.er-table__metric,
    .er-table th.er-table__metric,
    .er-table td.er-table__name {
      text-align: right;
    }

    .er-table__swatch {
      margin-right: 0;
      margin-left: 8px;
    }

    // Numbers stay LTR inside RTL prose, otherwise ranges like "12–18" flip.
    .er-stat__value,
    .er-cover__headline-value,
    .er-table td,
    .er-table th {
      unicode-bidi: isolate;
    }

    // Site, room and shift names run in their own direction, so a Latin name
    // like "122 Leadenhall" is not reordered into "Leadenhall 122".
    .er-cover__company,
    .er-cover__site-name,
    .er-cover__shift,
    .er-table td.er-table__name {
      unicode-bidi: plaintext;
    }

    .er-cover__highlight {
      border-right: 0;
      border-left: 1px solid #e2e8f0;

      &.is-first {
        padding-left: 18px;
        padding-right: 0;
      }

      &.is-last {
        padding-right: 18px;
        padding-left: 0;
        border-left: 0;
      }

      &.is-first.is-last {
        padding: 0;
        border: 0;
      }
    }
  }
}

.er-heading {
  margin: 0 0 20px;
  color: var(--er-accent, #0b3b5c);
  font-size: 26px;
}

.er-page.is-last-page {
  padding-bottom: 72px;
}

.er-cover {
  position: relative;

  &__inner {
    display: flex;
    flex-direction: column;
    height: 100%;

    // The photograph gives up height before the figures do, so a long site name
    // trims the photo instead of running over the footer.
    > *:not(.er-cover__hero) {
      flex-shrink: 0;
    }
  }

  &__letterhead {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    padding-bottom: 14px;
    border-bottom: 1px solid #e2e8f0;
  }

  &__brand-logo {
    flex-shrink: 0;
    width: 138px;
    height: 66px;
    object-fit: contain;
  }

  &__client {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    min-width: 0;
    text-align: right;
  }

  &__logo {
    flex-shrink: 0;
    max-height: 44px;
    max-width: 150px;
  }

  &__company {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
  }

  &__date {
    font-size: 13px;
    color: #64748b;
  }

  // Full-bleed band: the page padding is cancelled so the image runs edge to edge.
  &__hero {
    position: relative;
    flex: 1 1 auto;
    min-height: 300px;
    margin: 16px -36px 0;
    overflow: hidden;
    background: #0b1c2c;
  }

  &__hero-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 40%;
  }

  &__hero-fade {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 68%;
    background: linear-gradient(to bottom, rgba(11, 28, 44, 0) 0%, rgba(11, 28, 44, 0.72) 46%, #0b1c2c 100%);
  }

  &__caption {
    position: absolute;
    left: 36px;
    right: 36px;
    bottom: 26px;
    z-index: 1;
  }

  &__subject {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--er-accent-2, #d4a017);
  }

  &__period {
    margin-bottom: 4px;
    font-size: 15px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.82);
  }

  &__site-name {
    margin: 0;
    max-width: 820px;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 40px;
    font-weight: 400;
    line-height: 1.12;
    color: #fff;
  }

  &__rule {
    width: 48px;
    height: 2px;
    margin-top: 14px;
    background: var(--er-accent-2, #d4a017);
  }

  &__shift {
    margin: 10px 0 0;
    font-size: 15px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  &__chart-window {
    margin: 10px 0 0;
    color: #64748b;
    font-size: 12px;
  }

  &__headlines {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 28px;
    margin-top: 26px;

    &--2 {
      grid-template-columns: repeat(2, 1fr);
    }

    &--1 {
      grid-template-columns: 1fr;
      max-width: 280px;
    }
  }

  &__headline-value {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 36px;
    font-weight: 400;
    line-height: 1;
    color: var(--er-accent, #0b3b5c);
    font-variant-numeric: tabular-nums;

    span {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #64748b;
    }
  }

  &__headline-label {
    margin-top: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #64748b;
  }

  &__headline-change {
    margin-top: 4px;
    font-size: 13px;
    font-weight: 700;
    color: #475569;
    // Signed values keep their own direction so "+0.4" is not reordered to "0.4+".
    direction: ltr;
    unicode-bidi: isolate;

    &.inc-good {
      color: #15803d;
    }

    &.inc-bad {
      color: #b91c1c;
    }
  }

  &__spark {
    max-width: 240px;
    margin-top: 10px;
  }

  &__highlights {
    margin-top: 22px;
    padding-top: 14px;
    border-top: 1px solid #e2e8f0;
  }

  &__highlights-title {
    display: inline-block;
    margin: 0 0 12px;
    padding-bottom: 4px;
    border-bottom: 2px solid var(--er-accent-2, #d4a017);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--er-accent, #0b3b5c);
  }

  &__highlights-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);

    &--2 {
      grid-template-columns: repeat(2, 1fr);
    }

    &--1 {
      grid-template-columns: 1fr;
    }
  }

  &__highlight {
    margin: 0;
    padding: 0 18px;
    border-right: 1px solid #e2e8f0;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;

    &.is-first {
      padding-left: 0;
    }

    &.is-last {
      padding-right: 0;
      border-right: 0;
    }

    &.is-first.is-last {
      padding: 0;
      border: 0;
    }

    &.inc-good {
      color: #15803d;
    }

    &.inc-bad {
      color: #b91c1c;
    }
  }
}

.er-findings-board {
  margin-bottom: 28px;

  &__label {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;

    h3 {
      margin: 0;
      font-size: 15px;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: 0.01em;
    }
  }
}

.er-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  &--success {
    color: #166534;
    background: #dcfce7;
  }

  &--risk {
    color: #9a3412;
    background: #ffedd5;
  }
}

.er-findings-list {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e2e8f0;
}

.er-finding-row {
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #e2e8f0;

  &__index {
    width: 10px;
    height: 10px;
    margin-top: 6px;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.12);

    &--risk {
      background: #f59e0b;
      box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.14);
    }
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 4px;
  }

  &__body {
    min-width: 0;

    h4 {
      margin: 0;
      font-size: 15px;
      font-weight: 700;
      color: #0f172a;
    }

    p {
      margin: 0;
      color: #334155;
      font-size: 13.5px;
      line-height: 1.45;
    }
  }

  &__detail {
    margin-top: 3px !important;
    color: #64748b !important;
    font-size: 12.5px !important;
  }
}

.er-verdict {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;

  &--success {
    color: #166534;
    background: #dcfce7;
  }

  &--risk {
    color: #9a3412;
    background: #ffedd5;
  }
}

.er-table--averages {
  font-size: 12px;

  th,
  td {
    padding: 8px 8px;
  }
}

.er-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  overflow: hidden;
  border-radius: 10px;

  th,
  td {
    border: 1px solid #e2e8f0;
    padding: 9px 12px;
    text-align: center;
  }

  th:nth-child(n + 2),
  td:nth-child(n + 2) {
    font-variant-numeric: tabular-nums;
  }

  th {
    background: var(--er-accent, #0b3b5c);
    color: #fff;
    font-weight: 600;
    border-color: rgba(255, 255, 255, 0.12);
  }

  tbody tr:nth-child(even) td {
    background: #f8fafc;
  }

  td:first-child {
    font-weight: 600;
    color: #0f172a;
  }

  td.er-table__metric,
  td.er-table__name {
    text-align: left;
  }

  &__swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 8px;
    border-radius: 3px;
    vertical-align: middle;
  }

  .inc-good {
    color: #15803d;
    font-weight: 700;
  }

  .inc-bad {
    color: #b91c1c;
    font-weight: 700;
  }
}

.er-types-layout {
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 16px;
  align-items: center;

  &--compact {
    margin-bottom: 18px;

    .er-table th,
    .er-table td {
      padding: 5px 8px;
      font-size: 11px;
    }
  }
}

.er-table--rooms {
  th,
  td {
    padding: 6px 8px;
    font-size: 11px;
    line-height: 1.25;
  }

  th {
    white-space: normal;
  }
}

// A year of months runs across this table, so the value cells have to stay narrow
// while the metric names keep enough room to read on one line.
.er-table--months {
  table-layout: fixed;

  th,
  td {
    padding: 6px 4px;
    font-size: 10px;
    line-height: 1.25;
    white-space: nowrap;
  }

  th.er-table__metric,
  td.er-table__metric {
    width: 150px;
    padding-left: 10px;
    text-align: left;
    white-space: normal;
  }
}

.er-table--types {
  td.er-table__name {
    font-weight: 600;
  }

  .er-type-name {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .er-type-swatch {
    flex: 0 0 10px;
    width: 10px;
    height: 10px;
    border-radius: 2px;
  }

  .er-table__total td {
    font-weight: 700;
    background: #f1f5f9;
  }
}

.er-page-note {
  margin: -6px 0 18px;
  color: #475569;
  font-size: 13.5px;
  line-height: 1.45;
  max-width: 920px;
}

.er-trend-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  &--1 {
    grid-template-columns: 1fr;
  }
}

.er-trend-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 16px 14px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;

  .chart-container {
    margin-top: auto;
  }

  h3 {
    min-height: 38px;
    margin: 0 0 8px;
    font-size: 15px;
    line-height: 1.3;
    color: #0f172a;
  }

  &__value {
    display: flex;
    align-items: baseline;
    gap: 5px;
    font-size: 30px;
    font-weight: 700;
    line-height: 1;
    color: var(--er-accent, #0b3b5c);
    font-variant-numeric: tabular-nums;

    span {
      font-size: 12px;
      font-weight: 600;
      color: #64748b;
    }
  }

  &__meta {
    margin-top: 6px;
    font-size: 11px;
    line-height: 1.35;
    color: #64748b;
  }
}

.er-page--trends .er-trend-grid--3,
.er-page--trends .er-trend-grid--4 {
  flex: 1;
  min-height: 0;
  grid-template-rows: 1fr 1fr;
}

.er-trend-grid--3 .er-trend-card,
.er-trend-grid--4 .er-trend-card {
  min-height: 0;

  h3 {
    min-height: 0;
    margin-bottom: 6px;
  }

  .chart-container {
    flex: 1 1 auto;
    height: auto !important;
    min-height: 0;
  }
}

.er-table--shift-matrix {
  width: 100%;

  th,
  td {
    padding: 7px 8px;
    font-size: 11.5px;
    text-align: center;
  }

  th {
    font-weight: 700;
    line-height: 1.25;
    vertical-align: bottom;
  }

  td.er-table__metric,
  th.er-table__metric {
    text-align: left;
    white-space: nowrap;
  }

  thead tr:first-child th:not(.er-table__metric) {
    border-bottom: 0;
    font-size: 13px;
  }
}

.er-metric-strip {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 18px;

  &--1 {
    grid-template-columns: 1fr;
  }

  &--3 {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.er-stat {
  padding: 16px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;

  &__value {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 34px;
    font-weight: 700;
    line-height: 1;
    color: var(--er-accent, #0b3b5c);
    font-variant-numeric: tabular-nums;

    span {
      font-size: 14px;
      font-weight: 600;
      color: #64748b;
    }
  }

  &__label {
    margin-top: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
  }

  &__sub {
    margin-top: 2px;
    font-size: 12.5px;
    color: #64748b;
  }

}

.er-cv-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 12px;
}

.er-cv-wide {
  grid-column: 1 / -1;
}

.er-chart-panel {
  padding: 8px 4px 0;

  &--bordered {
    padding: 12px 14px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
  }

  &--doughnut {
    padding: 16px;
    overflow: hidden;
  }

  h4 {
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 700;
    color: #334155;
  }
}

.er-board-empty {
  margin: 24px 0 0;
  color: #64748b;
  font-size: 15px;
}

.er-hot-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;

  &--compact {
    gap: 12px;

    .er-table th,
    .er-table td {
      padding: 5px 7px;
      font-size: 10.5px;
    }
  }

  &--2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.er-hot-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;

  h3 {
    display: flex;
    align-items: flex-end;
    margin: 0 0 8px;
    height: 38px;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.25;
    overflow: hidden;
  }

  .er-table {
    flex: 1;
    height: 100%;
  }
}

.er-table--hot {
  td.er-table__name {
    font-weight: 600;
  }
}

.er-table--expired-slas {
  max-width: none;
}
</style>
